import { getCityPopulation, getCityName, getAllCities } from "./city-coords";
import type { PoliceEvent } from "./polisen.functions";
import { classifyEventCrimeStatus, type ClassifiableEvent } from "./crime-categories";

export interface CrimeSeverityLevel {
  level: 1 | 2 | 3 | 4 | 5;
  label: string;
  weight: number;
  description: string;
  examples: string[];
}

export const CRIME_SEVERITY_LEVELS: Record<number, CrimeSeverityLevel> = {
  5: {
    level: 5,
    label: "Extremt allvarligt våld",
    weight: 10.0,
    description: "Dödligt våld, skjutvapenanvändning och grova attentat.",
    examples: ["Mord/dråp", "Skottlossning", "Sprängning", "Människorov"],
  },
  4: {
    level: 4,
    label: "Allvarligt personvåld & vapen",
    weight: 5.0,
    description: "Grov misshandel, rån, sexualbrott och grova vapenbrott.",
    examples: ["Misshandel, grov", "Rån", "Våldtäkt", "Vapenlagen"],
  },
  3: {
    level: 3,
    label: "Integritetskränkande & inbrott",
    weight: 2.8,
    description: "Bostadsinbrott, misshandel av normalgraden, brand och olaga hot.",
    examples: ["Inbrott", "Misshandel", "Brand", "Olaga hot", "Våld/hot mot tjänsteman"],
  },
  2: {
    level: 2,
    label: "Förmögenhetsbrott & ordning",
    weight: 1.4,
    description: "Stöld, skadegörelse, narkotikabrott, knivbrott och fordonsstöld.",
    examples: ["Stöld/inbrott", "Narkotikabrott", "Knivlagen", "Skadegörelse", "Rattfylleri"],
  },
  1: {
    level: 1,
    label: "Mindre händelser & trafikkontroll",
    weight: 0.4,
    description: "Trafikolyckor, viltolyckor, trafikkontroller, ringa stölder och ordningsbot.",
    examples: ["Trafikolycka, vilt", "Trafikkontroll", "Trafikbrott", "Fylleri/LOB", "Försvunnen person"],
  },
};

export function getCrimeSeverity(type: string, event?: ClassifiableEvent): CrimeSeverityLevel {
  const t = type.toLowerCase();

  // Om händelsens text uttryckligen visar att det är en icke-brottslig händelse (t.ex. brand utan brottsmisstanke eller rutinkontroll)
  if (event) {
    const status = classifyEventCrimeStatus(event);
    if (status === "non_crime") {
      return CRIME_SEVERITY_LEVELS[1];
    }
  }

  if (
    t.includes("mord") ||
    t.includes("dråp") ||
    t.includes("skottlossning") ||
    t.includes("skjutning") ||
    t.includes("sprängning") ||
    t.includes("explosion") ||
    t.includes("detonation") ||
    t.includes("människorov") ||
    t.includes("grovt rån")
  ) {
    return CRIME_SEVERITY_LEVELS[5];
  }
  if (
    t.includes("våldtäkt") ||
    t.includes("grov misshandel") ||
    t.includes("misshandel, grov") ||
    t.includes("rån") ||
    t.includes("vapen") ||
    t.includes("kvinnofridskränkning") ||
    t.includes("allmänfarlig ödeläggelse")
  ) {
    return CRIME_SEVERITY_LEVELS[4];
  }
  if (
    t.includes("inbrott") ||
    t.includes("misshandel") ||
    t.includes("brand") ||
    t.includes("mordbrand") ||
    t.includes("vållande") ||
    t.includes("hot") ||
    t.includes("ofredande") ||
    t.includes("tjänsteman")
  ) {
    return CRIME_SEVERITY_LEVELS[3];
  }
  if (
    t.includes("stöld") ||
    t.includes("narkotika") ||
    t.includes("kniv") ||
    t.includes("skadegörelse") ||
    t.includes("tillgrepp") ||
    t.includes("bedrägeri") ||
    t.includes("rattfylleri")
  ) {
    return CRIME_SEVERITY_LEVELS[2];
  }
  return CRIME_SEVERITY_LEVELS[1];
}

export interface CitySafetyScore {
  citySlug: string;
  cityName: string;
  population: number;
  score: number; // 0–100
  rating: "A+" | "A" | "B" | "C" | "D" | "E";
  statusLabel: string;
  colorClass: string;
  badgeBg: string;
  textColor: string;
  totalEvents90d: number;
  perCapita90d: number; // per 1 000 invånare
  weightedLoadPerCapita: number;
  nationalRank: number; // 1 till 290 (1 = tryggast)
  totalMunicipalities: number;
  trendDirection: "improving" | "stable" | "deteriorating";
  trendPercentage: number;
  subScores: {
    personalSafety: number; // 0–100
    propertySafety: number; // 0–100
    publicOrder: number; // 0–100
  };
  severityDistribution: {
    level: number;
    label: string;
    count: number;
    pct: number;
    weight: number;
  }[];
}

/**
 * Beräknar Trygghetsindex (0–100) för en kommun utifrån dess händelser och befolkning.
 */
export function calculateCitySafetyIndex(
  citySlug: string,
  events: Array<{ type: string; datetime: string; name?: string | null; summary?: string | null }>,
  allCitiesRanking?: Array<{ slug: string; weightedLoad: number }>
): CitySafetyScore {
  const population = getCityPopulation(citySlug) || 15000;
  const cityName = getCityName(citySlug);

  const now = Date.now();
  const ninetyDaysAgo = new Date(now - 90 * 24 * 60 * 60 * 1000);
  const fortyFiveDaysAgo = new Date(now - 45 * 24 * 60 * 60 * 1000);

  // Filtrera händelser senaste 90 dagarna
  const recentEvents = events.filter((e) => new Date(e.datetime) >= ninetyDaysAgo);
  const recentHalf = events.filter((e) => new Date(e.datetime) >= fortyFiveDaysAgo);
  const earlierHalf = events.filter(
    (e) => new Date(e.datetime) >= ninetyDaysAgo && new Date(e.datetime) < fortyFiveDaysAgo
  );

  const totalEvents90d = recentEvents.length;
  const perCapita90d = Number(((totalEvents90d / population) * 1000).toFixed(2));

  // Beräkna allvarlighetsfördelning
  const severityCounts: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  let violenceCount = 0;
  let propertyCount = 0;
  let orderCount = 0;
  let totalWeightedScore = 0;

  for (const ev of recentEvents) {
    const sev = getCrimeSeverity(ev.type, ev);
    severityCounts[sev.level] = (severityCounts[sev.level] || 0) + 1;
    totalWeightedScore += sev.weight;

    if (sev.level >= 4) violenceCount++;
    else if (sev.level === 3 && (ev.type.includes("Inbrott") || ev.type.includes("Stöld"))) propertyCount++;
    else if (sev.level <= 2) orderCount++;
  }

  const weightedLoadPerCapita = Number(
    ((totalWeightedScore / (population / 1000))).toFixed(2)
  );

  // Beräkna grundpoäng 0–100
  // Kalibrerad mot Polisens operativa händelseflöde (medianbelastning ~1.0–2.0 per 1 000 inv/90d)
  let rawScore = 98 - (weightedLoadPerCapita * 14.5);

  // Trendberäkning
  const currentCount = recentHalf.length;
  const prevCount = earlierHalf.length;
  let trendPercentage = 0;
  let trendDirection: "improving" | "stable" | "deteriorating" = "stable";

  if (prevCount > 0) {
    trendPercentage = Math.round(((currentCount - prevCount) / prevCount) * 100);
  }

  if (trendPercentage <= -15) {
    trendDirection = "improving";
    rawScore += 3; // Minskat antal händelser ger trygghetsbonus
  } else if (trendPercentage >= 15) {
    trendDirection = "deteriorating";
    rawScore -= 3; // Ökat antal händelser sänker index
  }

  // Clampa poäng mellan 15 och 98
  const score = Math.max(15, Math.min(98, Math.round(rawScore)));

  // Betyg och färgkodning
  let rating: "A+" | "A" | "B" | "C" | "D" | "E" = "B";
  let statusLabel = "God trygghet";
  let colorClass = "text-emerald-600 dark:text-emerald-400";
  let badgeBg = "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30";
  let textColor = "text-emerald-600";

  if (score >= 85) {
    rating = "A+";
    statusLabel = "Mycket hög trygghet";
    colorClass = "text-emerald-500";
    badgeBg = "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30";
  } else if (score >= 75) {
    rating = "A";
    statusLabel = "Hög trygghet";
    colorClass = "text-teal-500";
    badgeBg = "bg-teal-500/15 text-teal-600 dark:text-teal-400 border-teal-500/30";
  } else if (score >= 62) {
    rating = "B";
    statusLabel = "God trygghet";
    colorClass = "text-blue-500";
    badgeBg = "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30";
  } else if (score >= 48) {
    rating = "C";
    statusLabel = "Måttlig trygghet";
    colorClass = "text-amber-500";
    badgeBg = "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30";
  } else if (score >= 35) {
    rating = "D";
    statusLabel = "Förhöjd brottsbelastning";
    colorClass = "text-orange-500";
    badgeBg = "bg-orange-500/15 text-orange-600 dark:text-orange-400 border-orange-500/30";
  } else {
    rating = "E";
    statusLabel = "Hög brottsbelastning";
    colorClass = "text-rose-500";
    badgeBg = "bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30";
  }

  // Delpoäng (0–100)
  const violenceRate = violenceCount / (population / 1000);
  const personalSafety = Math.max(
    10,
    Math.min(99, Math.round(98 - (violenceRate * 45)))
  );
  const propertyRate = propertyCount / (population / 1000);
  const propertySafety = Math.max(
    10,
    Math.min(99, Math.round(98 - (propertyRate * 30)))
  );
  const orderRate = orderCount / (population / 1000);
  const publicOrder = Math.max(
    10,
    Math.min(99, Math.round(98 - (orderRate * 18)))
  );

  // Beräkna nationell ranking
  let nationalRank = 145;
  const totalMunicipalities = allCitiesRanking?.length ?? 290;
  if (allCitiesRanking && allCitiesRanking.length > 0) {
    const sorted = [...allCitiesRanking].sort((a, b) => a.weightedLoad - b.weightedLoad);
    const idx = sorted.findIndex((c) => c.slug === citySlug);
    if (idx !== -1) {
      nationalRank = idx + 1;
    }
  }

  const severityDistribution = [5, 4, 3, 2, 1].map((lvl) => {
    const count = severityCounts[lvl] || 0;
    const pct = totalEvents90d > 0 ? Math.round((count / totalEvents90d) * 100) : 0;
    return {
      level: lvl,
      label: CRIME_SEVERITY_LEVELS[lvl].label,
      count,
      pct,
      weight: CRIME_SEVERITY_LEVELS[lvl].weight,
    };
  });

  return {
    citySlug,
    cityName,
    population,
    score,
    rating,
    statusLabel,
    colorClass,
    badgeBg,
    textColor,
    totalEvents90d,
    perCapita90d,
    weightedLoadPerCapita,
    nationalRank,
    totalMunicipalities,
    trendDirection,
    trendPercentage,
    subScores: {
      personalSafety,
      propertySafety,
      publicOrder,
    },
    severityDistribution,
  };
}
